import React from 'react';
import styled from 'styled-components';
import { useTranslation } from '@com/core/common/translations/i18n';

type TProps = {
    data: {
        name: string;
        purchase: string;
        purchaseLabel: string;
        position: {
            x: string;
            y: string;
        };
    };
    isActive: boolean;
};

const MapMarker = ({ data, isActive }: TProps): JSX.Element => {
    const { t } = useTranslation('common');

    return (
        <Container className={`mapMarker${isActive ? ' active' : ''}`} position={data.position}>
            <Marker>
                <div className="imageContainer">
                    <img src={`/images/landing/landingMapMarkers/${data.purchase}.png`} alt={data.purchaseLabel} />
                </div>
                <span className="desc">
                    <strong>{data.name}</strong> {t('just-ordered')}
                </span>
                <strong className="product">{data.purchaseLabel}</strong>
            </Marker>
            <Pulse>
                <div className="initialDot" />
                <div className="pulseContainer">
                    <div className="outer" />
                    <div className="inner">
                        <div className="dot" />
                    </div>
                </div>
            </Pulse>
        </Container>
    );
};

export default MapMarker;

const Marker = styled.div`
    background-color: ${({ theme }): string => theme.colors.bgPrimary};
    border: 1px solid ${({ theme }): string => theme.colors.border};
    border-radius: ${({ theme }): string => theme.metrics.borderRadius};
    display: inline-flex;
    flex-direction: column;
    justify-content: center;
    height: 72px;
    font-size: 0.875rem;
    position: relative;
    z-index: 5;
    opacity: 0;
    transition: opacity ease-in-out 1.2s;
    pointer-events: none;

    &:before,
    &:after {
        content: '';
        position: absolute;
        width: 0;
        height: 0;
        border: 8px solid transparent;
    }

    &:before {
        bottom: -16px;
        border-top-color: ${({ theme }): string => theme.colors.border};
    }

    &:after {
        bottom: -15px;
        border-top-color: ${({ theme }): string => theme.colors.bgPrimary};
    }

    .imageContainer {
        position: absolute;
        z-index: 2;
        top: -50%;
        width: 144px;
        height: 144px;
    }

    .desc {
        margin-bottom: 3px;
        color: ${({ theme }): string => theme.colors.bodyLight};

        strong {
            color: ${({ theme }): string => theme.colors.body};
            font-weight: 600;
        }
    }

    strong.product {
        color: ${({ theme }): string => theme.colors.brandPrimary};
    }
`;

const Pulse = styled.div`
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;

    .initialDot {
        border-radius: 100%;
        width: 6px;
        height: 6px;
        background-color: ${({ theme }): string => theme.colors.brandPrimary};
    }

    .pulseContainer {
        opacity: 0;
        transition: opacity ease-in-out 1.2s;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .inner {
        width: 16px;
        height: 16px;
        border-radius: 100%;
        background-color: ${({ theme }): string => theme.colors.brandPrimary};
        position: relative;
        z-index: 3;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .outer {
        position: absolute;
        background-color: green;
        border-radius: 50%;
    }

    .dot {
        border-radius: 100%;
        width: 4px;
        height: 4px;
        background-color: ${({ theme }): string => theme.colors.bgPrimary};
    }

    @keyframes loader-ripple {
        0% {
            width: 0;
            height: 0;
            opacity: 1;
        }
        100% {
            width: 40px;
            height: 40px;
            opacity: 0;
        }
    }
`;

const Container = styled.div<{ position: { x: string; y: string } }>`
    position: absolute;
    text-align: initial;
    display: flex;
    flex-direction: column;
    ${({ position }): string => `top: ${position.y}`};
    ${({ position, theme }): string => `${theme.left}: ${position.x}`};

    /* arrow/pulse on opposite ends */
    &:nth-child(odd) {
        align-items: flex-end;

        ${Marker} {
            ${({ theme: { left } }): string => `padding-${left}: 40px`};
            ${({ theme: { right } }): string => `padding-${right}: 32px`};

            &:before,
            &:after {
                ${({ theme: { right } }): string => `${right}: 12px`};
            }

            .imageContainer {
                ${({ theme: { left } }): string => `${left}: -72px`};
            }
        }
    }
    &:nth-child(even) {
        align-items: flex-start;

        ${Marker} {
            ${({ theme: { left } }): string => `padding-${left}: 32px`};
            ${({ theme: { right } }): string => `padding-${right}: 40px`};

            &:before,
            &:after {
                ${({ theme: { left } }): string => `${left}: 12px`};
            }

            .imageContainer {
                ${({ theme: { right } }): string => `${right}: -72px`};
            }
        }
    }

    &.active {
        ${Marker} {
            opacity: 1;
        }

        ${Pulse} {
            .pulseContainer {
                opacity: 1;
            }

            .outer {
                animation: loader-ripple 1s cubic-bezier(0, 0.2, 0.8, 1) infinite;
            }
        }
    }
`;
